import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faRotate, faDroplet } from '@fortawesome/free-solid-svg-icons';
import { merchantApi } from '../../api/config';
import { UiToggle } from '../ui';

/**
 * 💧 P2P settings (แท็บการเงิน) — รวม system_settings ของ P2P ไว้ที่เดียว
 * keys: p2p_enabled (routing), deposit_enabled (ระบบฝาก P2P), deposit_admin_bank_id (บัญชีเว็บ fallback),
 *       p2p_pair_ttl_minutes (นาที), p2p_max_matches_per_payout (จำนวนครั้งจับคู่สำเร็จสูงสุด/payout).
 *       โหมด non-custodial ถูกตรึงไว้ (ไม่ให้แก้จาก UI)
 */
interface Props {
  editValues: Record<string, any>;
  setEditValues: React.Dispatch<React.SetStateAction<any>>;
  saving: string | null;
  onSave: (key: string, valueOverride?: string) => void;
}

interface BankOption {
  id: string;
  name?: string;
  bank_code?: string;
  account_number?: string;
  is_active?: boolean;
  is_withdraw?: boolean;
  // ระดับร้านค้าที่ใช้บัญชีนี้ได้ ([] = ทุกระดับ) — มีเฉพาะ tenant ที่ migrate ระบบ tier แล้ว
  allowed_tier_ids?: string[];
}

const P2pSettingsCard: React.FC<Props> = ({ editValues, setEditValues, saving, onSave }) => {
  const [banks, setBanks] = useState<BankOption[]>([]);
  // เก็บทุกแถวไว้ด้วย — บัญชีที่ตั้งไว้แล้วอาจถูกปิดใช้งานทีหลัง (หลุดจาก dropdown แต่ยังต้องเตือน)
  const [allBanks, setAllBanks] = useState<BankOption[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await merchantApi.get('/admin/banks');
        const rows: BankOption[] = res.data.data || [];
        setAllBanks(rows);
        // บัญชีเว็บ fallback ต้องเป็นบัญชีรับเงิน (active, ไม่ใช่บัญชีถอน)
        setBanks(rows.filter(b => b.is_active && !b.is_withdraw));
      } catch { /* เงียบไว้ — dropdown จะว่าง */ }
    })();
  }, []);

  const p2pOn = editValues['p2p_enabled'] === 'true';
  const depositOn = editValues['deposit_enabled'] !== 'false'; // default เปิด
  const ttl = editValues['p2p_pair_ttl_minutes'] ?? '15';
  const maxMatches = editValues['p2p_max_matches_per_payout'] ?? '3';
  const adminBankId = editValues['deposit_admin_bank_id'] ?? '';
  // ⭐ safety-net bank ที่จำกัดระดับ / ไม่ active / เป็นบัญชีถอน → gateAdminBankByTier ตัดทิ้ง → ร้าน P2P ที่ไม่มี bank_merchants 503
  const selectedAdminBank = adminBankId !== '' ? allBanks.find(b => b.id === adminBankId) : undefined;
  const adminBankRestricted = !!selectedAdminBank && (
    (selectedAdminBank.allowed_tier_ids?.length ?? 0) > 0 ||
    selectedAdminBank.is_active === false ||
    selectedAdminBank.is_withdraw === true
  );

  const toggleRow = (key: string, on: boolean, title: string, desc: string) => (
    <div className="bg-gray-50 dark:bg-slate-700/40 rounded-xl p-3 ring-1 ring-inset ring-gray-100 dark:ring-slate-700 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-semibold text-gray-600 dark:text-slate-300">{title}</p>
        <p className="text-[11px] text-gray-400 dark:text-slate-500">{desc}</p>
      </div>
      <UiToggle
        checked={on}
        disabled={saving === key}
        ariaLabel={title}
        onChange={(next) => {
          const value = next ? 'true' : 'false';
          setEditValues((prev: any) => ({ ...prev, [key]: value }));
          onSave(key, value);
        }}
      />
    </div>
  );

  return (
    <div className="relative overflow-hidden rounded-3xl shadow-lg ring-1 ring-gray-100 dark:ring-slate-700 bg-white dark:bg-slate-800 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
      <div className="relative overflow-hidden px-5 py-4 text-white" style={{ background: 'var(--theme-gradient-info)' }}>
        <div className="pointer-events-none absolute -top-10 -right-8 h-28 w-28 rounded-full bg-white/20 blur-2xl" />
        <div className="relative flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur flex items-center justify-center text-lg shadow-inner shrink-0">
            <FontAwesomeIcon icon={faDroplet} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-[15px] leading-tight truncate">P2P (ฝากผ่าน Payout Liquidity)</h3>
            <p className="text-[11px] text-white/70 truncate">จับคู่เงินฝาก ↔ รายการถอน · เงินสดโอนตรง เว็บบันทึกเงินต้น+เก็บค่าธรรมเนียม</p>
          </div>
          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/15 ring-1 ring-white/25 backdrop-blur shrink-0">
            {p2pOn ? 'เปิด' : 'ปิด'}
          </span>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {toggleRow('p2p_enabled', p2pOn, 'เปิด P2P Routing', 'จับคู่ deposit กับ payout liquidity (ปิด = ฝากเข้าบัญชีเว็บทั้งหมด)')}
        {toggleRow('deposit_enabled', depositOn, 'เปิดระบบฝาก P2P', 'ปิด = หยุดรับ deposit P2P ชั่วคราว')}

        {/* บัญชีเว็บ fallback */}
        <div className="bg-gray-50 dark:bg-slate-700/40 rounded-xl p-3 ring-1 ring-inset ring-gray-100 dark:ring-slate-700">
          <p className="text-xs font-semibold text-gray-600 dark:text-slate-300 mb-2">
            บัญชีเว็บ fallback <span className="text-gray-400 dark:text-slate-500 font-normal">(ใช้เมื่อจับคู่ payout ไม่ได้ + ร้านไม่มีบัญชีของตัวเอง)</span>
          </p>
          <div className="flex items-center gap-2">
            <select
              value={adminBankId}
              onChange={(e) => {
                const v = e.target.value;
                setEditValues((prev: any) => ({ ...prev, deposit_admin_bank_id: v }));
                onSave('deposit_admin_bank_id', v);
              }}
              disabled={saving === 'deposit_admin_bank_id'}
              className="w-full py-2 px-3 rounded-lg text-sm font-medium border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600"
            >
              <option value="">— เลือกบัญชี —</option>
              {banks.map((b) => (
                <option key={b.id} value={b.id}>
                  {(b.bank_code || '').toUpperCase()} · {b.account_number} ({b.name})
                </option>
              ))}
            </select>
          </div>
          {adminBankRestricted && (
            <div className="mt-2 flex items-start gap-2 p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <span className="text-amber-500 text-sm leading-none">⚠️</span>
              <p className="text-[11px] text-amber-800 dark:text-amber-200 leading-relaxed">
                บัญชี fallback นี้จำกัดระดับร้านค้า/ปิดใช้งาน — ร้าน P2P ที่ระดับไม่ตรงหรือไม่มีบัญชีผูกจะฝากไม่ได้ (503)
              </p>
            </div>
          )}
        </div>

        {/* TTL นาที */}
        <div className="bg-gray-50 dark:bg-slate-700/40 rounded-xl p-3 ring-1 ring-inset ring-gray-100 dark:ring-slate-700">
          <p className="text-xs font-semibold text-gray-600 dark:text-slate-300 mb-2">
            เวลาจับคู่ (Window) <span className="text-gray-400 dark:text-slate-500 font-normal">(นาที)</span>
          </p>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="number"
                min="1"
                max="120"
                step="1"
                value={ttl}
                onChange={(e) => setEditValues((prev: any) => ({ ...prev, p2p_pair_ttl_minutes: e.target.value }))}
                className="w-full py-2 pl-3 pr-14 rounded-lg text-sm font-medium text-right border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-xs font-bold pointer-events-none">นาที</span>
            </div>
            <button
              type="button"
              onClick={() => onSave('p2p_pair_ttl_minutes')}
              disabled={saving === 'p2p_pair_ttl_minutes'}
              className="w-9 h-9 rounded-xl text-white flex items-center justify-center transition-all hover:shadow-lg disabled:opacity-50 flex-shrink-0"
              style={{ background: 'var(--theme-primary-gradient)' }}
            >
              <FontAwesomeIcon icon={saving === 'p2p_pair_ttl_minutes' ? faRotate : faSave} className={`text-xs ${saving === 'p2p_pair_ttl_minutes' ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* สูงสุด Matches ต่อ Payout */}
        <div className="bg-gray-50 dark:bg-slate-700/40 rounded-xl p-3 ring-1 ring-inset ring-gray-100 dark:ring-slate-700">
          <p className="text-xs font-semibold text-gray-600 dark:text-slate-300 mb-2">
            สูงสุด Matches ต่อ Payout <span className="text-gray-400 dark:text-slate-500 font-normal">(จับคู่สำเร็จได้กี่ครั้ง/ใบ · ครบแล้วปิด)</span>
          </p>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="number"
                min="1"
                max="50"
                step="1"
                value={maxMatches}
                onChange={(e) => setEditValues((prev: any) => ({ ...prev, p2p_max_matches_per_payout: e.target.value }))}
                className="w-full py-2 pl-3 pr-14 rounded-lg text-sm font-medium text-right border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-xs font-bold pointer-events-none">ครั้ง</span>
            </div>
            <button
              type="button"
              onClick={() => onSave('p2p_max_matches_per_payout')}
              disabled={saving === 'p2p_max_matches_per_payout'}
              className="w-9 h-9 rounded-xl text-white flex items-center justify-center transition-all hover:shadow-lg disabled:opacity-50 flex-shrink-0"
              style={{ background: 'var(--theme-primary-gradient)' }}
            >
              <FontAwesomeIcon icon={saving === 'p2p_max_matches_per_payout' ? faRotate : faSave} className={`text-xs ${saving === 'p2p_max_matches_per_payout' ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <p className="text-[11px] text-gray-400 dark:text-slate-500 leading-relaxed px-1">
          โหมด <b>non-custodial</b> (เงินสดโอนบัญชีต่อบัญชี ไม่ผ่านบัญชีเว็บ · ระบบบันทึกเงินต้นเข้ากระเป๋า — เครดิต Wallet ผู้ฝาก / หัก Payout ผู้รับสภาพคล่อง — และเก็บค่าธรรมเนียม) ถูกตรึงไว้เพื่อความปลอดภัยของยอดเงิน — แก้ไม่ได้จากหน้านี้
        </p>
      </div>
    </div>
  );
};

export default P2pSettingsCard;
